import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Grid,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin_red.svg";
import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import icon_info from "assets/images/icons/icn_Info_solid.svg";
import icon_info_hover from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as ProfileIcon } from "assets/images/icons/icn_dashboard_profile.svg";

import CollapseCard from "components/Collapse";
import TimePicker from "components/TimePicker";
import SearchInput from "components/SearchInput";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { WEEKDAYS, DEFAULT_RULE } from "pages/System/RealmSetting/components/ScheduledReboot/const";

const EditRuleDrawer = ({
  selectedRule = null,
  onClose,
  onApply,
  onDelete,
  showEditGeneral = true,
  devicesList = [],
}) => {
  const theme = useTheme();

  const textfieldStyle = {
    width: "286px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "30px",
      "& fieldset": {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.mainHover,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const pillContainerStyle = {
    display: "flex",
    border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
    borderRadius: "4px",
    overflow: "hidden",
    height: "26px",
    alignItems: "center",
    width: "fit-content",
  };

  const getPillItemStyle = (isActive) => ({
    px: 3,
    height: "100%",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    cursor: showEditGeneral ? "pointer" : "not-allowed",
    backgroundColor: isActive ? theme.palette.primary.main : "#fff",
    color: isActive ? "#fff" : theme.palette.primary.greySecound,
    userSelect: "none",
    transition: "background-color 0.2s, color 0.2s",
  });

  const [name, setName] = useState("");
  const [enable, setEnable] = useState(true);
  const [applyType, setApplyType] = useState("models"); // "models" or "devices"
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [frequencyType, setFrequencyType] = useState("Daily");
  const [selectedDays, setSelectedDays] = useState([]);
  const [time, setTime] = useState("00:00");

  const [modelSearch, setModelSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceCategoryTab, setDeviceCategoryTab] = useState("All");

  useEffect(() => {
    if (selectedRule) {
      setName(selectedRule.name || "");
      setEnable(selectedRule.enable !== false);
      setTime(selectedRule.time || "00:00");

      if (selectedRule.modelNames && selectedRule.modelNames.length > 0) {
        setApplyType("models");
        setSelectedModels(selectedRule.modelNames);
        setSelectedDevices([]);
      } else {
        setApplyType("devices");
        setSelectedDevices(selectedRule.deviceIds || []);
        setSelectedModels([]);
      }

      const freq = selectedRule.frequency || "";
      if (freq.toLowerCase() === "daily" || freq === "0,1,2,3,4,5,6") {
        setFrequencyType("Daily");
        setSelectedDays([]);
      } else {
        setFrequencyType("Weekly");
        setSelectedDays(freq ? freq.split(",").map(Number).filter((n) => !isNaN(n)) : []);
      }
    }
  }, [selectedRule]);

  const processedModels = useMemo(() => {
    const modelsMap = {};
    devicesList.forEach((dev) => {
      const devInfo = dev.info || {};
      const model = devInfo.modelName || "Unknown";
      const isOnline = devInfo.linkStatus === "online";
      if (!modelsMap[model]) {
        modelsMap[model] = { name: model, total: 0, online: 0 };
      }
      modelsMap[model].total += 1;
      if (isOnline) {
        modelsMap[model].online += 1;
      }
    });
    return Object.values(modelsMap);
  }, [devicesList]);

  const filteredModels = useMemo(() => {
    return processedModels.filter((m) =>
      m.name.toLowerCase().includes(modelSearch.toLowerCase())
    );
  }, [processedModels, modelSearch]);

  const filteredDevices = useMemo(() => {
    return devicesList.filter((dev) => {
      const devInfo = dev.info || {};
      const category = devInfo.deviceType || "";
      const model = devInfo.modelName || "";
      const isAP = category === "ap" || model.startsWith("DAP") || model.startsWith("AP");
      const isSwitch = category === "switch" || model.startsWith("DGS") || model.startsWith("DXS") || model.startsWith("DES");

      if (deviceCategoryTab === "Access Point" && !isAP) return false;
      if (deviceCategoryTab === "Switch" && !isSwitch) return false;

      const nameMatch = (devInfo.deviceName || "").toLowerCase().includes(deviceSearch.toLowerCase());
      const macMatch = (devInfo.macAddress || "").toLowerCase().includes(deviceSearch.toLowerCase());

      return nameMatch || macMatch;
    });
  }, [devicesList, deviceSearch, deviceCategoryTab]);

  const handleModelToggle = (modelName) => {
    setSelectedModels((prev) =>
      prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName]
    );
  };

  const handleDeviceToggle = (mac) => {
    setSelectedDevices((prev) =>
      prev.includes(mac) ? prev.filter((d) => d !== mac) : [...prev, mac]
    );
  };

  const handleDayToggle = (dayVal) => {
    setSelectedDays((prev) =>
      prev.includes(dayVal)
        ? prev.filter((d) => d !== dayVal)
        : [...prev, dayVal]
    );
  };

  const hasChanges = () => {
    if (!selectedRule) return false;

    if ((name || "") !== (selectedRule.name || "")) return true;

    const originalEnable = selectedRule.enable !== false;
    if (enable !== originalEnable) return true;

    const originalApplyType = (selectedRule.modelNames && selectedRule.modelNames.length > 0) ? "models" : "devices";
    if (applyType !== originalApplyType) return true;

    if (applyType === "models") {
      const originalModels = [...(selectedRule.modelNames || [])].sort();
      const currentModels = [...selectedModels].sort();
      if (JSON.stringify(originalModels) !== JSON.stringify(currentModels)) return true;
    }

    if (applyType === "devices") {
      const originalDevices = [...(selectedRule.deviceIds || [])].sort();
      const currentDevices = [...selectedDevices].sort();
      if (JSON.stringify(originalDevices) !== JSON.stringify(currentDevices)) return true;
    }

    const freq = selectedRule.frequency || "";
    const originalIsDaily = freq.toLowerCase() === "daily" || freq === "0,1,2,3,4,5,6";
    const currentIsDaily = frequencyType === "Daily";
    if (currentIsDaily !== originalIsDaily) return true;

    if (!currentIsDaily) {
      const originalDays = freq ? freq.split(",").map(Number).filter((n) => !isNaN(n)).sort() : [];
      const currentDays = [...selectedDays].sort();
      if (JSON.stringify(originalDays) !== JSON.stringify(currentDays)) return true;
    }

    if (time !== (selectedRule.time || "00:00")) return true;

    return false;
  };

  const checkFormValid = () => {
    if (!name.trim()) return false;
    if (applyType === "models" && selectedModels.length === 0) return false;
    if (applyType === "devices" && selectedDevices.length === 0) return false;
    if (frequencyType === "Weekly" && selectedDays.length === 0) return false;
    return true;
  };

  const handleSave = () => {
    if (!checkFormValid()) return;

    let freqValue = "daily";
    if (frequencyType === "Weekly") {
      freqValue = selectedDays.sort((a, b) => a - b).join(",");
    }

    let finalDeviceIds = [];
    if (applyType === "models") {
      finalDeviceIds = devicesList
        .filter((d) => d.info && selectedModels.includes(d.info.modelName))
        .map((d) => d.info.macAddress)
        .filter(Boolean);
    } else {
      finalDeviceIds = selectedDevices;
    }

    const payload = {
      name,
      enable,
      modelNames: applyType === "models" ? selectedModels : [],
      deviceIds: finalDeviceIds,
      frequency: freqValue,
      time,
    };

    if (selectedRule) {
      payload.id = selectedRule.id;
    }

    onApply(payload);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          pb: "80px",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <ProfileIcon style={{ width: "60px", height: "60px" }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{
                fontWeight: theme.typography.customFontWeight[3],
                fontSize: theme.typography.customFontSize[5],
                color: theme.palette.primary.greyPrimary,
              }}
            >
              {name}
            </span>
            <Box display={'flex'} alignItems="center" mt={1}>
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  border: `1.5px solid ${theme.palette.primary.main}`,
                  padding: "4px 12px",
                  height: "27px",
                  fontSize: theme.typography.customFontSize[0],
                  borderRadius: theme.shape.customBorderRadius[7],
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  display: "flex",
                }}
              >
                {applyType === "models" ? "Device Models" : "Specific Devices"}
              </Box>
              <Box display={'flex'} alignItems={'center'} gap={1} sx={{ marginLeft: '10px', fontSize: "14px", color: theme.palette.primary.greyPrimary }}>
                <Box>Assigned</Box>
                <Tooltip
                  title={
                    applyType === "models"
                      ? (selectedModels.join(", ") || "No models selected")
                      : (selectedDevices
                        .map((mac) => {
                          const dev = devicesList.find((d) => d.info?.macAddress === mac);
                          return dev ? (dev.name || dev.info?.modelName || mac) : mac;
                        })
                        .join(", ") || "No devices selected")
                  }
                  placement="bottom"
                  arrow
                >
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {applyType === "models"
                      ? `${selectedModels.length} Model(s)`
                      : `${selectedDevices.length} Device(s)`
                    }
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content Form */}
        <Box sx={{ px: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Basic */}
          <CollapseCard title="Basic">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ width: "120px", fontSize: "14px", color: theme.palette.primary.greyPrimary }}>
                  Rule Name
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  disabled={!showEditGeneral}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={textfieldStyle}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ width: "120px", fontSize: "14px", color: theme.palette.primary.greyPrimary }}>
                  Status
                </Typography>
                <Box
                  sx={{
                    cursor: showEditGeneral ? "pointer" : "not-allowed",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  onClick={() => {
                    if (showEditGeneral) {
                      setEnable(!enable);
                    }
                  }}
                >
                  {enable ? <SwitchOnIcon /> : <SwitchOffIcon />}
                </Box>
              </Box>
            </Box>
          </CollapseCard>

          {/* Apply to */}
          <CollapseCard title="Apply to">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
              <Box sx={{ display: "flex", borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                <Box
                  onClick={() => showEditGeneral && setApplyType("models")}
                  sx={{
                    px: 3,
                    py: 1,
                    cursor: showEditGeneral ? "pointer" : "not-allowed",
                    fontWeight: applyType === "models" ? "bold" : "normal",
                    borderBottom: applyType === "models" ? `3px solid ${theme.palette.primary.main}` : "none",
                    color: applyType === "models" ? theme.palette.primary.main : theme.palette.primary.greySecound,
                  }}
                >
                  Device Models
                </Box>
                <Box
                  onClick={() => showEditGeneral && setApplyType("devices")}
                  sx={{
                    px: 3,
                    py: 1,
                    cursor: showEditGeneral ? "pointer" : "not-allowed",
                    fontWeight: applyType === "devices" ? "bold" : "normal",
                    borderBottom: applyType === "devices" ? `3px solid ${theme.palette.primary.main}` : "none",
                    color: applyType === "devices" ? theme.palette.primary.main : theme.palette.primary.greySecound,
                  }}
                >
                  Specific Devices
                </Box>
              </Box>

              {applyType === "models" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
                    <SearchInput
                      value={modelSearch}
                      onChange={setModelSearch}
                      placeholder="Search by model name"
                    />
                    <OutlineButton
                      height="32px"
                      disabled={!showEditGeneral}
                      onClick={() => setSelectedModels([])}
                    >
                      Clear Selections
                    </OutlineButton>
                  </Box>

                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.primary.greyThread}`,
                      borderRadius: "4px",
                      maxHeight: "260px",
                      overflowY: "auto",
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#fafafa", borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                          <th style={{ padding: "10px", width: "40px", textAlign: "center" }}>
                            <Checkbox
                              disabled={!showEditGeneral}
                              checked={filteredModels.length > 0 && filteredModels.every((m) => selectedModels.includes(m.name))}
                              indeterminate={
                                filteredModels.some((m) => selectedModels.includes(m.name)) &&
                                !filteredModels.every((m) => selectedModels.includes(m.name))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const newSels = Array.from(new Set([...selectedModels, ...filteredModels.map((m) => m.name)]));
                                  setSelectedModels(newSels);
                                } else {
                                  const filteredNames = filteredModels.map((m) => m.name);
                                  setSelectedModels(selectedModels.filter((m) => !filteredNames.includes(m)));
                                }
                              }}
                            />
                          </th>
                          <th style={{ padding: "10px", textAlign: "left", color: "#666" }}>Model</th>
                          <th style={{ padding: "10px", textAlign: "left", color: "#666" }}>Devices Amount</th>
                          <th style={{ padding: "10px", textAlign: "right", color: "#666", paddingRight: "20px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredModels.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                              No data available
                            </td>
                          </tr>
                        ) : (
                          filteredModels.map((m) => (
                            <tr key={m.name} style={{ borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                              <td style={{ padding: "6px", textAlign: "center" }}>
                                <Checkbox
                                  disabled={!showEditGeneral}
                                  checked={selectedModels.includes(m.name)}
                                  onChange={() => handleModelToggle(m.name)}
                                />
                              </td>
                              <td style={{ padding: "6px", textAlign: "left", fontWeight: 500 }}>{m.name}</td>
                              <td style={{ padding: "6px", textAlign: "left", color: "#666" }}>{m.total} device(s)</td>
                              <td style={{ padding: "6px", textAlign: "right", paddingRight: "20px", color: "#666" }}>
                                {m.online} online
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </Box>
                  <Typography sx={{ mt: 1, fontSize: "12px", color: theme.palette.primary.greySecound }}>
                    {selectedModels.length} device models selected
                  </Typography>
                </Box>
              )}

              {applyType === "devices" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
                    <SearchInput
                      value={deviceSearch}
                      onChange={setDeviceSearch}
                      placeholder="Search by name or MAC address"
                    />
                    <OutlineButton
                      height="32px"
                      disabled={!showEditGeneral}
                      onClick={() => setSelectedDevices([])}
                    >
                      Clear Selections
                    </OutlineButton>
                  </Box>

                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.primary.greyThread}`,
                      borderRadius: "4px",
                      maxHeight: "260px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table style={{ width: "100%", minWidth: "750px", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#fafafa", borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                          <th style={{ padding: "10px", width: "40px", textAlign: "center" }}>
                            <Checkbox
                              disabled={!showEditGeneral}
                              checked={filteredDevices.length > 0 && filteredDevices.every((d) => selectedDevices.includes(d.info?.macAddress))}
                              indeterminate={
                                filteredDevices.some((d) => selectedDevices.includes(d.info?.macAddress)) &&
                                !filteredDevices.every((d) => selectedDevices.includes(d.info?.macAddress))
                              }
                              onChange={(e) => {
                                const devMacs = filteredDevices.map((d) => d.info?.macAddress).filter(Boolean);
                                if (e.target.checked) {
                                  setSelectedDevices(Array.from(new Set([...selectedDevices, ...devMacs])));
                                } else {
                                  setSelectedDevices(selectedDevices.filter((d) => !devMacs.includes(d)));
                                }
                              }}
                            />
                          </th>
                          <th style={{ padding: "10px", textAlign: "left", color: "#666" }}>Device Name</th>
                          <th style={{ padding: "10px", textAlign: "center", color: "#666" }}>Access Point</th>
                          <th style={{ padding: "10px", textAlign: "center", color: "#666" }}>Switch</th>
                          <th style={{ padding: "10px", textAlign: "left", color: "#666" }}>MAC Address</th>
                          <th style={{ padding: "10px", textAlign: "right", color: "#666", paddingRight: "20px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDevices.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                              No data available
                            </td>
                          </tr>
                        ) : (
                          filteredDevices.map((d) => {
                            const devInfo = d.info || {};
                            const mac = devInfo.macAddress;
                            const nameVal = devInfo.deviceName || "Device";
                            const isOnline = devInfo.linkStatus === "online";
                            const model = devInfo.modelName || "";
                            const category = devInfo.deviceType || "";
                            const isAP = category === "ap" || model.startsWith("DAP") || model.startsWith("AP");
                            const isSwitch = category === "switch" || model.startsWith("DGS") || model.startsWith("DXS") || model.startsWith("DES");

                            return (
                              <tr key={mac} style={{ borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                                <td style={{ padding: "6px", textAlign: "center" }}>
                                  <Checkbox
                                    disabled={!showEditGeneral}
                                    checked={selectedDevices.includes(mac)}
                                    onChange={() => handleDeviceToggle(mac)}
                                  />
                                </td>
                                <td style={{ padding: "6px", textAlign: "left", fontWeight: 500 }}>{nameVal}</td>
                                <td style={{ padding: "6px", textAlign: "center", color: theme.palette.primary.main, fontWeight: "bold" }}>
                                  {isAP ? "✓" : ""}
                                </td>
                                <td style={{ padding: "6px", textAlign: "center", color: theme.palette.primary.main, fontWeight: "bold" }}>
                                  {isSwitch ? "✓" : ""}
                                </td>
                                <td style={{ padding: "6px", textAlign: "left", color: "#666" }}>{mac}</td>
                                <td style={{ padding: "6px", textAlign: "right", paddingRight: "20px" }}>
                                  <span style={{ color: "#666" }}>
                                    {isOnline ? "Online" : "Offline"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </Box>
                  <Typography sx={{ mt: 1, fontSize: "12px", color: theme.palette.primary.greySecound }}>
                    {selectedDevices.length} devices selected
                  </Typography>
                </Box>
              )}
            </Box>
          </CollapseCard>

          {/* Schedule */}
          <CollapseCard title="Schedule">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ width: "120px", fontSize: "14px", color: theme.palette.primary.greyPrimary }}>
                  Frequency
                </Typography>
                <Box sx={pillContainerStyle}>
                  <Box
                    onClick={() => showEditGeneral && setFrequencyType("Daily")}
                    sx={getPillItemStyle(frequencyType === "Daily")}
                  >
                    Daily
                  </Box>
                  <Box
                    onClick={() => showEditGeneral && setFrequencyType("Weekly")}
                    sx={getPillItemStyle(frequencyType === "Weekly")}
                  >
                    Weekly
                  </Box>
                </Box>
              </Box>

              {frequencyType === "Weekly" && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ width: "150px" }} />
                  <Grid container spacing={1} sx={{ maxWidth: "450px" }}>
                    {WEEKDAYS.map((day) => {
                      const isSelected = selectedDays.includes(day.value);
                      return (
                        <Grid item key={day.value}>
                          <Box
                            onClick={() => showEditGeneral && handleDayToggle(day.value)}
                            sx={{
                              paddingTop: "4px",
                              paddingBottom: "4px",
                              width: "60px",
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              borderRadius: "16px",
                              fontSize: "13px",
                              cursor: showEditGeneral ? "pointer" : "not-allowed",
                              border: `1px solid ${isSelected ? theme.palette.primary.main : "#cccccc"}`,
                              backgroundColor: isSelected ? theme.palette.primary.main : "#ffffff",
                              color: isSelected ? "#ffffff" : theme.palette.primary.greyPrimary,
                              userSelect: "none",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {day.label}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ width: "120px", display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "14px", color: theme.palette.primary.greyPrimary }}>
                    Time
                  </Typography>
                  <Tooltip title="Controller's local time zone." placement="bottom" arrow>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundImage: `url(${icon_info})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundImage: `url(${icon_info_hover})`,
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
                <TimePicker
                  value={time}
                  onChange={setTime}
                  disabled={!showEditGeneral}
                />
              </Box>
            </Box>
          </CollapseCard>

          {/* Delete Rule */}
          {selectedRule && showEditGeneral && (
            <Box
              onClick={() => onDelete(selectedRule.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                color: "#ff4d4f",
                cursor: "pointer",
                fontSize: "14px",
                width: "fit-content",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <RemoveIcon style={{ width: 16, height: 16 }} />
              <span>Remove Rule</span>
            </Box>
          )}
        </Box>
      </Box>

      {/* Sticky footer action buttons */}
      {showEditGeneral && hasChanges() && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: "16px",
            backgroundColor: "primary.lightBlue",
            bottom: 0,
            left: 0,
            borderTop: `1px solid ${theme.palette.primary.greyThread}`,
            zIndex: 100,
            boxSizing: "border-box",
          }}
        >
          <OutlineButton
            onClick={onClose}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            disabled={!checkFormValid()}
            onClick={handleSave}
            width="100px"
            height="32px"
          >
            Apply
          </SolidButton>
        </Box>
      )}
    </Box>
  );
};

export default EditRuleDrawer;
